import {
  Button,
  Box,
  Table,
  Link,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Card,
  CardContent,
  CardActions,
  Divider,
  Grid,
  Stack,
  Typography,
  Skeleton,
  Chip,
  IconButton,
  Avatar,
  Container,
  useTheme,
  alpha,
  Tooltip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import StarIcon from "@mui/icons-material/Star";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { useState } from "react";
import { useSelector } from "react-redux";
import DashboardLayout from "@wordform/common/layouts/dashboard";
import { useAppDispatch } from "@wordform/core/hooks/useStore";
import { RootState } from "@wordform/core/store/store";
import usePaddlePlans from "./use-paddle";
import { useEffect } from "react";
import { useDialog } from "@wordform/hooks/use-dialog";
import { CancelSubscriptionDialog } from "./cancel-subscription-dialog";
import { ActivatePlanDialog } from "./activate-plan-dialog";
import { PaddleSubscription, PaddleAvailableAddon } from "./paddle.slice";
import { toast } from "react-hot-toast";
import axios from "axios";
import Subscriptions from "../subscriptions";

declare global {
  interface Window {
    Paddle: any;
  }
}

interface SubscriptionTableProps {
  activeSubs: PaddleSubscription[];
  cancelSubDialog: any;
  manageSubscriptionUrl?: string;
}

interface PaddleCustomerAuthToken {
  token: string;
  customerId: string;
}

const getCustomerAuthToken = (response: any): PaddleCustomerAuthToken | null => {
  const result = response.data?.result;

  if (!result?.token || !result?.customerId) {
    return null;
  }

  return {
    token: result.token,
    customerId: result.customerId,
  };
};

const SubscriptionsTable: React.FC<SubscriptionTableProps> = ({ activeSubs, cancelSubDialog, manageSubscriptionUrl }) => {
  const theme = useTheme();
  const subscriptions = useSelector((state: RootState) => state.auth.subscriptions?.subscriptions ?? []);

  const getLocalSubscription = (paddleSubscriptionId: string) =>
    subscriptions.find((subscription) => subscription.paddleSubscriptionId === paddleSubscriptionId);

  const isCancellingAtPeriodEnd = (paddleSubscriptionId: string) =>
    getLocalSubscription(paddleSubscriptionId)?.cardLinked === false;
  
  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'active':
        return <CheckCircleIcon sx={{ fontSize: 16 }} />;
      case 'trialing':
        return <InfoIcon sx={{ fontSize: 16 }} />;
      case 'past_due':
        return <WarningIcon sx={{ fontSize: 16 }} />;
      case 'canceled':
      case 'cancelled':
        return <CancelIcon sx={{ fontSize: 16 }} />;
      default:
        return undefined;
    }
  };
  
  const getStatusColor = (status: string) => {
    switch(status) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'past_due':
        return 'error';
      case 'canceled':
      case 'cancelled':
        return 'default';
      default:
        return 'warning';
    }
  };

  return (
    <Card 
      elevation={0}
      sx={{ 
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          p: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Active Subscriptions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeSubs.length} {activeSubs.length === 1 ? 'subscription' : 'subscriptions'} active
            </Typography>
          </Box>
          {manageSubscriptionUrl && (
            <Button 
              href={manageSubscriptionUrl} 
              target="_blank" 
              variant="contained"
              startIcon={<ManageAccountsIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3
              }}
            >
              Open Paddle billing portal
            </Button>
          )}
        </Box>
      </Box>
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Subscription</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Access & Renewal</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeSubs.map((row, index) => {
              const cancellingAtPeriodEnd = isCancellingAtPeriodEnd(row.paddleSubscriptionId);

              return (
                <TableRow
                  key={"subscription-" + row.priceId + "-" + index}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) }
                  }}
                >
                  <TableCell>
                    <Typography variant="body1" fontWeight={500}>
                      {row.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Paddle ID: {row.paddleSubscriptionId}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Since {row.subscribed}
                    </Typography>
                  </TableCell>
                <TableCell>
                  <Chip
                    icon={row.kind === "primary" ? <StarIcon sx={{ fontSize: 14 }} /> : <LocalOfferIcon sx={{ fontSize: 14 }} />}
                    label={row.kind === "primary" ? "Plan" : "Addon"}
                    size="small"
                    variant="outlined"
                    color={row.kind === "primary" ? "primary" : "secondary"}
                    sx={{ fontWeight: 500 }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getStatusIcon(cancellingAtPeriodEnd ? "cancelled" : row.status)}
                    label={cancellingAtPeriodEnd ? "Cancels at period end" : row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                    size="small"
                    color={getStatusColor(cancellingAtPeriodEnd ? "cancelled" : row.status) as any}
                    sx={{ 
                      fontWeight: 500,
                      ...(row.inTrial && {
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        color: theme.palette.info.main,
                        borderColor: theme.palette.info.main
                      })
                    }}
                  />
                  {row.inTrial && (
                    <Typography variant="caption" sx={{ ml: 1, color: "info.main", fontWeight: 500 }}>
                      Trial Period
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body1" fontWeight={600} color="primary">
                    {row.amount}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {row.nextBilledAt
                      ? cancellingAtPeriodEnd
                        ? `Access ends ${row.nextBilledAt}`
                        : `Renews ${row.nextBilledAt}`
                      : "N/A"}
                  </Typography>
                </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};


interface AvailableAddonsGridProps {
  availableAddons: PaddleAvailableAddon[];
  activateAddonDialog?: any; // Made optional since we're not using it anymore
}

interface AvailablePlansGridProps {
  availablePlans: PaddleAvailableAddon[];
  activePlans: PaddleSubscription[];
  activatePlanDialog: any;
}

interface OneTimeOption {
  id: string;
  title: string;
  description: string[];
  price: string;
  subscriptionId?: string;
}

// Reusable Pricing Card Component
interface PricingCardProps {
  title: string;
  description: string[];
  price: string;
  buttonLabel: string;
  onButtonClick: () => void;
  cardType: 'plan' | 'addon' | 'one-time';
  isFreeTrial?: boolean;
  buttonVariant?: 'contained' | 'outlined';
  icon?: React.ReactNode;
}

const PricingCard: React.FC<PricingCardProps> = ({ 
  title, 
  description, 
  price, 
  buttonLabel, 
  onButtonClick, 
  cardType,
  isFreeTrial = false,
  buttonVariant = 'contained',
  icon
}) => {
  const theme = useTheme();
  
  const getCardTypeLabel = () => {
    switch(cardType) {
      case 'plan':
        return 'Subscription Plan';
      case 'addon':
        return 'Addon';
      case 'one-time':
        return 'One-Time Purchase';
      default:
        return '';
    }
  };
  
  const getIcon = () => {
    if (icon) return icon;
    switch(cardType) {
      case 'plan':
        return <StarIcon />;
      case 'addon':
        return <LocalOfferIcon />;
      case 'one-time':
        return <ShoppingCartIcon />;
      default:
        return <CreditCardIcon />;
    }
  };
  
  const getBottomText = () => {
    if (cardType === 'one-time') {
      return 'One-time payment';
    }
    return null;
  };
  
  return (
    <Card 
      elevation={0}
      sx={{ 
        height: '100%',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        transition: 'all 0.3s',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[4],
          borderColor: theme.palette.primary.main
        }
      }}
    >
      {isFreeTrial && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            bgcolor: theme.palette.primary.main,
            color: 'white',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.75rem',
            fontWeight: 600
          }}
        >
          FREE TRIAL
        </Box>
      )}
      
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Box textAlign="center" mb={2}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              margin: '0 auto',
              mb: 2
            }}
          >
            {getIcon()}
          </Avatar>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {title}
          </Typography>
          <Chip 
            label={getCardTypeLabel()} 
            size="small" 
            color="primary"
            sx={{ fontWeight: 500 }}
          />
        </Box>
        
        <Box mb={3}>
          {description.map((line, index) => (
            <Typography 
              key={index} 
              variant="body2" 
              color="text.secondary" 
              align="center"
              paragraph
            >
              {line}
            </Typography>
          ))}
        </Box>
        
        <Box textAlign="center">
          <Typography 
            variant="h4" 
            fontWeight={700} 
            color="primary"
            gutterBottom
          >
            {price}
          </Typography>
          {getBottomText() && (
            <Typography variant="caption" color="text.secondary">
              {getBottomText()}
            </Typography>
          )}
        </Box>
      </CardContent>
      
      <CardActions sx={{ p: 3, pt: 0 }}>
        <Button 
          fullWidth
          onClick={onButtonClick} 
          variant={buttonVariant}
          color="primary"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            py: 1.5
          }}
        >
          {buttonLabel}
        </Button>
      </CardActions>
    </Card>
  );
};

const oneTimeOptions: OneTimeOption[] = [
  {
    id: "pri_01jtjrd9etakx0423a1v1v2x1z",
    title: "10 Articles One Time",
    description: ["Generate 10 articles", "Pay once, use anytime"],
    price: "$20",
  },
  {
    id: "pri_01jtjreymta8yhh6mf0m9jtd2w",
    title: "100 Articles One Time",
    description: ["Generate 100 articles", "Pay once, use anytime"],
    price: "$147",
  },
  {
    id: "pri_01jtjrgez67fvgtd9epmng0nk4",
    title: "200 Articles One Time",
    description: ["Generate 200 articles", "Pay once, use anytime"],
    price: "$197",
  },
];

const AvailableAddonsGrid: React.FC<AvailableAddonsGridProps> = ({ availableAddons, activateAddonDialog }) => {
  const authState = useSelector((state: RootState) => state.auth);
  const { apiUrl } = useSelector((state: RootState) => state.home);
  
  const handleAddonPurchase = async (addon: PaddleAvailableAddon) => {
    if (!window.Paddle) {
      toast.error("Payment system not initialized. Please refresh the page.");
      return;
    }
    
    try {
      const checkoutConfig: any = {
        items: [{
          priceId: addon.priceId,
          quantity: 1
        }],
        customer: {
          email: authState.user?.email
        },
        customData: {
          subscriptionId: addon.subscriptionId
        },
        settings: {
          successUrl: window.location.href,
          displayMode: 'overlay',
          theme: 'light',
          locale: 'en'
        },
        eventCallback: function(data: any) {
          if (data.name === 'checkout.completed') {
            toast.success("Purchase successful! Activation may take up to a minute. You'll receive a confirmation email shortly.", {
              duration: 8000
            });
          }
        }
      };

      try {
        const response = await axios.post(`${apiUrl}/paddle/auth-token`);
        const customerAuthToken = getCustomerAuthToken(response);

        if (customerAuthToken) {
          checkoutConfig.customer = { id: customerAuthToken.customerId };
          checkoutConfig.customerAuthToken = customerAuthToken.token;
          checkoutConfig.settings.allowLogout = false;
        }
      } catch (error) {
        console.log("No auth token available, proceeding with regular checkout");
      }

      window.Paddle.Checkout.open(checkoutConfig);
    } catch (error) {
      console.error("Error opening checkout:", error);
      toast.error("Failed to initialize checkout. Please try again.");
    }
  };

  return (
    <Box py={3}>
      <Grid container spacing={3}>
        {availableAddons.map((row) => (
          <Grid item xs={12} sm={6} lg={4} key={"addon-" + row.priceId}>
            <PricingCard
              title={row.name}
              description={row.description?.split("\n") || []}
              price={row.amount}
              buttonLabel={row.name.includes("Trial") ? "Start Trial" : "Get Addon"}
              onButtonClick={() => handleAddonPurchase(row)}
              cardType="addon"
              isFreeTrial={row.name.includes("Trial")}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const AvailablePlansGrid: React.FC<AvailablePlansGridProps> = ({ availablePlans, activePlans, activatePlanDialog }) => {
  // Filter out plans that are already active
  const filteredPlans = availablePlans.filter(plan =>
    !activePlans.some(activePlan => activePlan.priceId === plan.priceId)
  );

  // Don't render if no plans to show
  if (filteredPlans.length === 0) {
    return null;
  }

  return (
    <Box py={3}>
      <ActivatePlanDialog
        onClose={activatePlanDialog.handleClose}
        open={activatePlanDialog.open}
        plan={activatePlanDialog.data}
      />

      <Grid container spacing={3}>
        {filteredPlans.map((row) => (
          <Grid item xs={12} sm={6} lg={4} key={"plan-" + row.priceId}>
            <PricingCard
              title={row.name}
              description={row.description?.split("\n") || []}
              price={row.amount}
              buttonLabel={row.name.includes("Trial") ? "Start Trial" : "Subscribe"}
              onButtonClick={() => activatePlanDialog.handleOpen(row)}
              cardType="plan"
              isFreeTrial={row.name.includes("Trial")}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

interface OneTimePurchaseProps {
  onPurchase: (option: OneTimeOption) => void;
  subscriptionId?: string;
}

const OneTimePurchaseGrid: React.FC<OneTimePurchaseProps> = ({ onPurchase, subscriptionId }) => {
  const authState = useSelector((state: RootState) => state.auth);
  const { apiUrl } = useSelector((state: RootState) => state.home);
  
  const handleOneTimePurchase = async (option: OneTimeOption) => {
    if (!window.Paddle) {
      toast.error("Payment system not initialized. Please refresh the page.");
      return;
    }
    
    try {
      let checkoutConfig: any = {
        items: [{
          priceId: option.id,
          quantity: 1
        }],
        customer: {
          email: authState.user?.email
        },
        customData: {
          subscriptionId: subscriptionId
        },
        settings: {
          successUrl: window.location.href,
          displayMode: 'overlay',
          theme: 'light',
          locale: 'en'
        },
        eventCallback: function(data: any) {
          if (data.name === 'checkout.completed') {
            toast.success("Purchase successful! Credits may take up to a minute to appear. You'll receive a confirmation email shortly.", {
              duration: 8000
            });
          }
        }
      };
      
      // If user has a subscription, get auth token for saved payment method
      if (subscriptionId) {
        try {
          const response = await axios.post(`${apiUrl}/paddle/auth-token`);
          const customerAuthToken = getCustomerAuthToken(response);

          if (customerAuthToken) {
            checkoutConfig.customer = { id: customerAuthToken.customerId };
            checkoutConfig.customerAuthToken = customerAuthToken.token;
            checkoutConfig.settings.allowLogout = false;
          }
        } catch (error) {
          console.log("No auth token available, proceeding with regular checkout");
        }
      }
      
      window.Paddle.Checkout.open(checkoutConfig);
    } catch (error) {
      console.error("Error opening checkout:", error);
      toast.error("Failed to open checkout. Please try again.");
    }
  };

  return (
    <Box py={3}>
      <Grid container spacing={3}>
        {oneTimeOptions.map((option) => (
          <Grid item xs={12} sm={6} lg={4} key={`one-time-${option.id}`}>
            <PricingCard
              title={option.title}
              description={option.description}
              price={option.price}
              buttonLabel="Purchase Now"
              onButtonClick={() => handleOneTimePurchase(option)}
              cardType="one-time"
              buttonVariant="outlined"
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const PaddleView = () => {
  const dispatch = useAppDispatch();
  const authState = useSelector((state: RootState) => state.auth);
  const { apiUrl } = useSelector((state: RootState) => state.home);
  const paddleState = useSelector((state: RootState) => state.paddle);
  const [loading, setLoading] = useState<boolean>(false);
  const { getPlans } = usePaddlePlans();
  const cancelSubDialog = useDialog();
  const activateAddonDialog = useDialog();
  const activatePlanDialog = useDialog();
  const theme = useTheme();
  const manageSubscriptionUrl = paddleState?.paddle?.manageSubscriptionUrl;
  
  // Combine all active subscriptions (plans and addons)
  const allActiveSubscriptions = [
    ...(paddleState?.paddle?.activePlans || []),
    ...(paddleState?.paddle?.activeAddons || [])
  ];
  const latestInactiveSubscription =
    authState.subscriptions?.subscriptions && authState.subscriptions.subscriptions.length > 0
      ? [...authState.subscriptions.subscriptions]
          .filter((subscription) => subscription.status !== "active" && subscription.status !== "trial")
          .sort((a, b) => {
            const aDate = new Date(a.endDate || a.rebillDate || a.startDate).getTime();
            const bDate = new Date(b.endDate || b.rebillDate || b.startDate).getTime();

            return bDate - aDate;
          })[0]
      : null;

  // Get the primary subscription ID for saved payment methods
  const primarySubscriptionId = paddleState?.paddle?.activePlans[0]?.paddleSubscriptionId;

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const fetchPlans = async () => {
      if (authState.user?.id) {
        setLoading(true);
        await getPlans();
        setLoading(false);
      }
    };
    fetchPlans();
  }, [authState.user?.id]);

  if (loading) {
    return (
      <DashboardLayout active={"billing"} name="Billing">
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Stack spacing={3}>
            <Skeleton variant="text" width={200} height={40} />
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            <Skeleton variant="text" width={150} height={40} />
            <Grid container spacing={3}>
              {[1, 2, 3].map((i) => (
                <Grid item xs={12} sm={6} lg={4} key={i}>
                  <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout active={"billing"} name="Billing">
      <CancelSubscriptionDialog
        onClose={cancelSubDialog.handleClose}
        open={cancelSubDialog.open}
        sub={cancelSubDialog.data}
      />
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Stack spacing={6}>
          {/* Page Header */}
          <Box>
            <Typography 
              variant="h4" 
              fontWeight={700}
              gutterBottom
              sx={{ color: theme.palette.text.primary }}
            >
              Billing & Subscriptions
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
            >
              Manage your subscription plans, addons, and billing settings
            </Typography>
          </Box>

          {/* Status Alerts */}
          {allActiveSubscriptions.length > 0 && (
            <Stack spacing={2}>
              {allActiveSubscriptions.some(sub => sub.inTrial) && (
                <Alert 
                  severity="info" 
                  icon={<InfoIcon />}
                  sx={{ 
                    borderRadius: 2,
                    '& .MuiAlert-message': { width: '100%' }
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Trial Period Active
                    </Typography>
                    <Typography variant="body2">
                      Your trial will automatically convert to a paid subscription unless cancelled.
                    </Typography>
                  </Box>
                </Alert>
              )}
              {allActiveSubscriptions.some(sub => sub.status === "past_due") && (
                <Alert 
                  severity="warning" 
                  icon={<WarningIcon />}
                  sx={{ 
                    borderRadius: 2,
                    '& .MuiAlert-message': { width: '100%' }
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Payment Failed
                    </Typography>
                    <Typography variant="body2">
                      Please update your payment method to avoid service interruption.
                    </Typography>
                  </Box>
                </Alert>
              )}
              {allActiveSubscriptions.some(sub => sub.status === "canceled" || sub.status === "cancelled") && (
                <Alert 
                  severity="info"
                  sx={{ 
                    borderRadius: 2,
                    '& .MuiAlert-message': { width: '100%' }
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Subscription Cancelled
                    </Typography>
                    <Typography variant="body2">
                      Your cancelled subscriptions will remain active until their expiration date.
                    </Typography>
                  </Box>
                </Alert>
              )}
            </Stack>
          )}

          {/* Active Subscriptions */}
          {allActiveSubscriptions.length > 0 ? (
            <SubscriptionsTable
              activeSubs={allActiveSubscriptions}
              cancelSubDialog={cancelSubDialog}
              manageSubscriptionUrl={manageSubscriptionUrl}
            />
          ) : (
            <Card 
              elevation={0}
              sx={{ 
                p: 6,
                textAlign: 'center',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.02)
              }}
            >
              <CreditCardIcon 
                sx={{ 
                  fontSize: 64, 
                  color: theme.palette.action.disabled,
                  mb: 2 
                }} 
              />
              <Typography variant="h6" gutterBottom>
                No Active Subscriptions
              </Typography>
              <Stack spacing={2} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Your latest subscription status is still shown below so you can see when access ended.
                </Typography>
                {latestInactiveSubscription && (
                  <Typography variant="body2" color="text.secondary">
                    Most recent status: <b>{latestInactiveSubscription.status}</b>
                    {latestInactiveSubscription.endDate &&
                      `, ended on ${new Date(latestInactiveSubscription.endDate).toLocaleDateString()}`}
                    .
                  </Typography>
                )}
                <Button
                  href="https://wordform.ai/evergreen/"
                  target="_blank"
                  rel="noreferrer"
                  variant="contained"
                >
                  Reactivate Subscription
                </Button>
              </Stack>
            </Card>
          )}

          {allActiveSubscriptions.length === 0 && <Subscriptions />}
          
          {/* Available Plans Section */}
          {paddleState?.paddle?.availablePlans?.length > 0 && (
            <Box>
              <Box mb={3}>
                <Typography 
                  variant="h5" 
                  fontWeight={600}
                  gutterBottom
                >
                  Available Plans
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose the plan that best fits your needs
                </Typography>
              </Box>
              <AvailablePlansGrid
                availablePlans={paddleState.paddle.availablePlans}
                activePlans={paddleState.paddle.activePlans}
                activatePlanDialog={activatePlanDialog}
              />
            </Box>
          )}

          {allActiveSubscriptions.length === 0 && paddleState?.paddle?.availablePlans?.length === 0 && (
            <Card
              elevation={0}
              sx={{
                p: 4,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
              }}
            >
              <Stack spacing={2} alignItems="flex-start">
                <Typography variant="h5" fontWeight={600}>
                  Start a Wordform subscription
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Compare the current plans and complete your new subscription through Paddle.
                </Typography>
                <Button
                  href="https://wordform.ai/evergreen/"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  startIcon={<ShoppingCartIcon />}
                >
                  View Subscription Plans
                </Button>
              </Stack>
            </Card>
          )}

          {/* Available Addons Section */}
          {paddleState?.paddle?.availableAddons?.length > 0 && (
            <Box>
              <Box mb={3}>
                <Typography 
                  variant="h5" 
                  fontWeight={600}
                  gutterBottom
                >
                  Power-Up with Addons
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Enhance your subscription with additional features
                </Typography>
              </Box>
              <AvailableAddonsGrid
                availableAddons={paddleState.paddle.availableAddons}
                activateAddonDialog={activateAddonDialog}
              />
            </Box>
          )}

          {/* One-time Purchases */}
          <Box>
            <Box mb={3}>
              <Typography 
                variant="h5" 
                fontWeight={600}
                gutterBottom
              >
                One-Time Article Packs
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {primarySubscriptionId ? 
                  "Purchase article credits using your saved payment method" : 
                  "Purchase article credits without a subscription"}
              </Typography>
            </Box>
            <OneTimePurchaseGrid 
              onPurchase={() => {}} 
              subscriptionId={primarySubscriptionId} 
            />
          </Box>
        </Stack>
      </Container>
    </DashboardLayout>
  );
};

export default PaddleView;
